import { inject, injectable } from "inversify";
import Messages from "../Messages";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import IStatesManager from "../states/IStatesManager";
import SystemConfig from "../system/SystemConfig";
import FetchAjax from "./FetchAjax";
import ConnectAjaxAdd from "./ajax/ConnectAjaxAdd";
import ConnectAjaxBalance from "./ajax/ConnectAjaxBalance";
import ConnectAjaxClose from "./ajax/ConnectAjaxClose";
import ConnectAjaxEnd from "./ajax/ConnectAjaxEnd";
import ConnectAjaxGetButtonOpt from "./ajax/ConnectAjaxGetButtonOpt";
import ConnectAjaxGetDeviceOpt from "./ajax/ConnectAjaxGetDeviceOpt";
import ConnectAjaxGetGameOpt from "./ajax/ConnectAjaxGetGameOpt";
import ConnectAjaxGetGameSessions from "./ajax/ConnectAjaxGetGameSessions";
import ConnectAjaxGetSessions from "./ajax/ConnectAjaxGetSessions";
import ConnectAjaxInit from "./ajax/ConnectAjaxInit";
import ConnectAjaxGetLastOpts from "./ajax/ConnectAjaxLastOpts";
import ConnectAjaxLogic from "./ajax/ConnectAjaxLogic";
import ConnectAjaxPlatformSettings from "./ajax/ConnectAjaxPlatformSettings";
import ConnectAjaxReplay from "./ajax/ConnectAjaxReplay";
import ConnectAjaxSetButtonOpt from "./ajax/ConnectAjaxSetButtonOpt";
import ConnectAjaxSetDeviceOpt from "./ajax/ConnectAjaxSetDeviceOpt";
import ConnectAjaxSetGameOpt from "./ajax/ConnectAjaxSetGameOpt";
import ConnectCloseBeacon from "./ajax/ConnectCloseBeacon";
import IAjaxConnect from "./ajax/IAjaxConnect";
import ConnectSocket from "./socket/ConnectSocket";

export const FETCH_TYPES = {
	SETTINGS: "settings", // new sessions
	INIT: "init",
	ADD: "add",
	UPDATE: "update",
	CLOSE: "close",
	PICK: "pick",
	BALANCE: "balance",
	REGULATION: "regulation",
	WEBSOCKET: "websocket",
	GET_DEVICE_OPT: "get_device_opt",
	SET_DEVICE_OPT: "set_device_opt",
	GET_GAME_OPT: "get_game_opt",
	SET_GAME_OPT: "set_game_opt",
	GET_LAST_OPTS: "get_last_opts",
	GET_BUTTON_OPT: "get_button_opt",
	SET_BUTTON_OPT: "set_button_opt",
	GET_SESSIONS: "getSessions",
	GET_GAME_SESSIONS: "getGameSessions",
	REPLAY: "replay",
	LOGIC: "logic",
	PLATFORM_SETTINGS: "platformSettings",
};

export function parseURL(
	url: string,
	params /*, { casinoId, gameId, sessionId, roundId, pickId }*/
): string {
	for (let key in params) {
		url = replaceParam(url, key, params[key]);
	}

	return url;

	// url = replaceParam(url, 'casino_id', casinoId);
	// url = replaceParam(url, 'game_id', gameId);
	// url = replaceParam(url, 'session_id', sessionId);
	// url = replaceParam(url, 'pick_id', pickId);
	// return replaceParam(url, 'round_id', roundId);
}

function replaceParam(url, param, value) {
	if (value != null) {
		url = url.replace("{" + param + "}", value);
	}
	return url;
}

@injectable()
export default class Connect {
	private _config: SystemConfig;
	private _messages: Messages;
	private _fetch: FetchAjax;

	private statesManager: IStatesManager;
	private connectFactory: (type?: string) => IAjaxConnect;

	constructor(
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.SystemMessages) messages: Messages,
		@inject(SERVICE_IDENTIFIER.FetchAjax) fetch: FetchAjax,
		@inject(SERVICE_IDENTIFIER.StatesManager) statesManager: IStatesManager,
		// @inject(SERVICE_IDENTIFIER.ConnectInit) connectInit: IAjaxConnect
		@inject(SERVICE_IDENTIFIER.ConnectFactory)
		connectFactory: (type?: string) => IAjaxConnect
	) {
		this._config = config;
		this._messages = messages;
		this._fetch = fetch;
		this.connectFactory = connectFactory;
		this.statesManager = statesManager;
	}

	async init(params) {
		let connect = this.connectFactory(FETCH_TYPES.INIT) as ConnectAjaxInit;
		return await connect.fetch(params);
	}

	async add(params) {
		let connect = this.connectFactory(FETCH_TYPES.ADD) as ConnectAjaxAdd;
		return await connect.fetch(params);
	}

	async end(params) {
		let connect = this.connectFactory(FETCH_TYPES.UPDATE) as ConnectAjaxEnd;
		return await connect.fetch(params);
	}

	async close(params) {
		let connect = this.connectFactory(FETCH_TYPES.CLOSE) as ConnectAjaxClose;
		return await connect.fetch(params);
	}

	async balance(params = {}) {
		let connect = this.connectFactory(
			FETCH_TYPES.BALANCE
		) as ConnectAjaxBalance;
		return await connect.fetch(params);
	}

	beacon(params) {
		let connect = new ConnectCloseBeacon(this._config);
		return connect.fetch(params);
	}

	async getSessions(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_SESSIONS
		) as ConnectAjaxGetSessions;
		return await connect.fetch(params);
	}

	async getGameSessions(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_GAME_SESSIONS
		) as ConnectAjaxGetGameSessions;
		return await connect.fetch(params);
	}

	async socket(params) {
		let connect = this.connectFactory(FETCH_TYPES.WEBSOCKET) as ConnectSocket;

		try {
			return await connect.fetch(params);
		} catch (error) {
			console.error(error);
		}
	}
	async getDeviceOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_DEVICE_OPT
		) as ConnectAjaxGetDeviceOpt;
		return await connect.fetch(params);
	}

	async setDeviceOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.SET_DEVICE_OPT
		) as ConnectAjaxSetDeviceOpt;
		return await connect.fetch(params);
	}

	async getGameOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_GAME_OPT
		) as ConnectAjaxGetGameOpt;
		return await connect.fetch(params);
	}

	async setGameOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.SET_GAME_OPT
		) as ConnectAjaxSetGameOpt;
		return await connect.fetch(params);
	}

	async getLastOpts(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_LAST_OPTS
		) as ConnectAjaxGetLastOpts;
		return await connect.fetch(params);
	}

	async getButtonOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.GET_BUTTON_OPT
		) as ConnectAjaxGetButtonOpt;
		return await connect.fetch(params);
	}

	async setButtonOpt(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.SET_BUTTON_OPT
		) as ConnectAjaxSetButtonOpt;
		return await connect.fetch(params);
	}

	async replay(params) {
		let connect = this.connectFactory(FETCH_TYPES.REPLAY) as ConnectAjaxReplay;
		return await connect.fetch(params);
	}

	async logic(params) {
		let connect = this.connectFactory(FETCH_TYPES.LOGIC) as ConnectAjaxLogic;
		return await connect.fetch(params);
	}

	async platformSettings(params) {
		let connect = this.connectFactory(
			FETCH_TYPES.PLATFORM_SETTINGS
		) as ConnectAjaxPlatformSettings;
		return await connect.fetch(params);
	}
}

export function getFormData(data = {}) {
	let formData = new FormData();
	for (let key in data) {
		formData.append(key, data[key]);
	}

	return formData;
}
